// The cowboy. stepPlayer is the function both ends run: the server for real, the client
// to predict its own cowboy. Everything in here must depend only on (state, input, world).
// Anything that needs other entities (damage, priming, revives) lives in game.ts.

import { DT, clamp, secs, len2, len3, wrapAngle } from './core';
import { PU, COOLANT_MUL, PU_FIRE_EL } from './powerups';
import { CLASSES, ClassId, EYE_HEIGHT, WARP_CLOAK, GAS_SLOW_MUL,
  BLINK_HOLD, HAMMER_MAX, HAMMER_WINDUP, POUND_FALL, VDASH_CHARGES, VDASH_GAP, VDASH_HEAT, VDASH_IFRAMES, VDASH_RECHARGE, VDASH_SPEED, VDASH_TICKS,
  WARP_CD, WARP_DIST, WARP_HEAT, WARP_IFRAMES, WARP_REFLECT, WARP_SELF_DMG, HEAT_RED as HEAT_RED_FRAC, HULL_MAX, ICE_SLOW_MAX_STACKS, ICE_SLOW_PER_STACK, ICE_STACK_DECAY_SECS, PLAYER_HEIGHT, PLAYER_RADIUS,
  FLAMEOUT_DELAY, FLAMEOUT_VENT, FLIGHT_CEIL, HEAT_FIRE_DPS, HEAT_FIRE_SELF, HEAT_RED, HEAT_RED_DPS, RELIGHT, SHIELD_REGEN_HEAT, SHIELD_REGEN_HEAT_CEIL, shieldDelayFor,
  GETUP, KNOCK_FRICTION } from './defs';
import { NO_GROUND, WATER, World } from './world';
import { DraftKind, HAND_MAX, Mods, cardMods, hullMaxOf, newMods, shieldMaxOf } from './cards';
import { PLAYER_BURN_DPS } from './elements';
import { BREACH, BUILDING_FIRE_TICKS } from './destruct';
import { ADREN_MELEE_CD, ADREN_SPEED, CHUG_TICKS, CHUG_WALK_MUL, HEAL_TICKS, WHISKEY_START } from './whiskey';
import { CLASS_LOADOUT, FAN_BLEED, FAN_BLOOM, FAN_INTERVAL, FAN_SPREAD, Fire, GRENADES, Perk, PLASMA_FULL_MUL, PLASMA_MIN, PLASMA_PARTIAL_MIN, ShotMod, SPIN_FAST, SPIN_SLOW, SPIN_TICKS, STEADY_BONUS, STEADY_TICKS, TIME_SLOW_MUL, GRENADE_MAX, NO_WEAPON, Proj, THROW_CD, WSlot, WeaponDef, weaponDef } from './weapons';

export const enum Btn {
  Jump = 1, Fire = 2, Reload = 4, Melee = 8, Dodge = 16, Special = 32, Interact = 64, Pause = 128, Fly = 256,
  /** weapons lane: swap primary/secondary, throw a grenade, pull the special weapon */
  Swap = 512, Throw = 1024, SpecialWeapon = 16384,
  /** movement lane: L2 aim, L3 sprint, L1 hover */
  Aim = 2048, Sprint = 4096, Hover = 8192,
  /** run lane: fire the held power-up (touchpad / B) */
  PowerUp = 32768,
  /** heal lane: chug a bottle of whiskey (D-pad ◀ / H). Past 16 bits: inputs go up as u32 */
  Drink = 65536,
}
/** what a cowboy can still do mid-chug: nothing but walk (and pause) */
const CHUG_KEEP = Btn.Pause;
/** ADS bit (owned by the movement lane; 2048): tightens spread to the weapon's ADS cone */
export const AIM_BIT = 2048;

/** melee cadence, the gap allowed between flourishes, buffered press, steps */
export const MELEE_CD = secs(0.5); // James 2026-10-21: blade flourishes chain every 0.5 s, same damage per hit
export const COMBO_WINDOW = secs(0.65);
export const MELEE_BUFFER = secs(0.3);
export const COMBO_STEPS = 3;

export interface InputFrame {
  tick: number;
  mx: number; // strafe -1..1
  my: number; // forward -1..1
  /** aim: the line from the eye through the crosshair (shots, facing) */
  yaw: number;
  pitch: number;
  /** the camera's yaw: what the stick steers along (never the parallax-corrected aim) */
  myaw: number;
  buttons: number;
  /** server tick (fractional) the player was looking at; for lag compensation */
  viewTick: number;
}
export const emptyInput = (tick: number): InputFrame => ({ tick, mx: 0, my: 0, yaw: 0, pitch: 0, myaw: 0, buttons: 0, viewTick: 0 });

export const enum Phase { Alive = 0, Down = 1, Dead = 2 }
export const enum Move { Ground = 0, Air = 1, Hover = 2, Flight = 3, Dodge = 4, Special = 5, Land = 6 }

/** Every numeric field here is reconciled from the server, in this order. */
export const PLAYER_FIELDS = [
  'x', 'y', 'z', 'vx', 'vy', 'vz', 'yaw', 'pitch',
  'hp', 'shield', 'heat', 'shieldDelay', 'heatGrace', 'onFire',
  'mag', 'reloadT', 'fireCd', 'meleeCd', 'meleeT', 'dodgeT', 'dodgeCd', 'dodgeX', 'dodgeZ',
  'specialT', 'specialCd', 'move', 'grounded', 'hoverT', 'lastGx', 'lastGy', 'lastGz',
  'phase', 'phaseT', 'paused', 'skyT', 'reviveT', 'chainT', 'chainN', 'cloakT', 'stillT', 'iframes',
  'cls', 'airT', 'prevButtons',
  // status effects from enemy procs (server-set, reconciled like hp): ice stacks + decay, gas slow
  'iceN', 'iceT', 'gasT',
  // movement: sprint latch, flight grammar, hover hold, dodge kind, landings, ADS
  'sprint', 'jumpHold', 'jumpTap', 'coyote', 'jumpBuf', 'hoverMode', 'hoverY', 'stallT', 'dodgeK', 'landT', 'landK', 'aimOn', 'airDashN', 'dodgeBuf', 'fallTopY', 'stepY',
  // loadout (sim/weapons.ts): active slot, weapon per slot, stored mags, special-shot bank,
  // grenade type / count / bank, throw cooldown, charge + burst state, slot to return to
  'wSlot', 'w0', 'w1', 'w2', 'mag0', 'mag1', 'mag2', 'bank', 'nade', 'nades', 'nadeBank', 'throwCd', 'chargeT', 'burstN', 'prevSlot', 'cookT',
  // power-ups (sim/powerups.ts): held slots + elements (+1), second-slot cap, the running buff,
  // its element (+1) and clock, the overshield layer, the stim heal-over-time
  'pu', 'puEl', 'pu2', 'pu2El', 'puCap', 'puAct', 'puActEl', 'puT', 'over', 'stimT',
  // caught in a Time grenade: half speed while it runs (it slows the posse too)
  'timeT',
  // the class energy blade (melee lane): drawn 0/1, flourish step (0 = the draw strike, then 1..3 looping),
  // ticks left to chain the next flourish, a melee press buffered through the end of the last swing
  'blade', 'combo', 'comboT', 'meleeBuf',
  // gun identities (sim/weapons.ts Perk): Auto MG spin, sniper steady hold, Big Iron fan bloom
  'spin', 'steady', 'bloom',
  // whiskey (sim/whiskey.ts): ticks left in the chug, bottles in the belt (the heal after it is stimT)
  'drinkT', 'bottles',
  // the flameout (Godot flight_heat.gd): > 0 = dead time after the engine sputtered out, -1 = venting hard
  // until the pack cools to RELIGHT, 0 = clear. No jets until it's clear.
  'flameT',
  // weight (defs.ts KNOCK_*): input frozen after any hit (the shove coasts), floored by a big one (on the ground,
  // then GETUP of get-up), and the cooldown before you can be floored again. game.ts hurtPlayer sets them.
  'hitStunT', 'knockT', 'knockCd',
  // movement lane, the Godot verbs (CONTROLS.md): a slide (the ○ out of a sprint) and the speed a roll carries
  'slide', 'rollV',
  // class verbs (CLASSES.md): ticks R3 has been held (the Phantom's blink, the Juggernaut's hammer), the warp's
  // reflection window, the Valkyrie's dash charges and recharge clock, her ground pound and the height it started at
  'meleeHold', 'reflectT', 'dashN', 'dashT', 'pound', 'poundY',
  // wildcard cards (sim/cards.ts): the hand (card numbers, 0 empty), the offer on the table (3 + its DraftKind), hull
  // lost for good (Mortal Coil), the once-per-run / once-per-island / once-per-chain charges used, the round counter
  // (Overcharge), the pack-fire clock (Overcook Insurance), Quick Vent's window
  'card0', 'card1', 'card2', 'card3', 'card4', 'card5', 'card6', 'card7', 'card8', 'card9', 'card10', 'card11',
  'offer0', 'offer1', 'offer2', 'offerKind', 'hpLost', 'phoenixUsed', 'horseshoeUsed', 'eagleUsed', 'chainMissUsed', 'echoUsed', 'skyLandUsed', 'shotN', 'fireT', 'quickVentT',
  // the elements (sim/elements.ts): the element on each gun (Elem + 1, 0 none) and its level 1-4; burning (8 HP/s through
  // the shield), acid-softened (+15 % hull damage), wet (in water lately)
  'el0', 'el1', 'elL0', 'elL1', 'burnT', 'acidT', 'wetT',
  // Gunsmith levels on each gun (0-3), kills since the last hit taken (Bartender's Bell), Conduit: Acid's window
  'wl0', 'wl1', 'killsNoHit', 'acidBoostT',
  /** the grenade element's level (munitions caches, CACHES in ROSTER.md): 0/1 = L1, up to 3 */
  'nadeLevel',
] as const;
export type PlayerField = (typeof PLAYER_FIELDS)[number];
export type PlayerState = Record<PlayerField, number> & { id: number; slot: number; name: string };

export function newPlayer(id: number, slot: number, name: string, cls: ClassId, world: World): PlayerState {
  const p = { id, slot, name } as PlayerState;
  for (const f of PLAYER_FIELDS) p[f] = 0;
  p.cls = cls;
  p.fallTopY = -1e9;
  const l = CLASS_LOADOUT[cls];
  p.w0 = l.primary; p.w1 = l.secondary; p.w2 = NO_WEAPON; p.nade = l.grenade;
  p.bottles = WHISKEY_START;
  p.dashN = VDASH_CHARGES;
  respawnAt(p, world, world.spawnPoint(slot));
  return p;
}

/** The gun in the active slot. */
export function weaponOf(p: PlayerState): WeaponDef {
  const id = p.wSlot === WSlot.Special ? p.w2 : p.wSlot === WSlot.Secondary ? p.w1 : p.w0;
  return weaponDef(id === NO_WEAPON ? p.w0 : id);
}
/** Gunsmith (sim/cards.ts): the level on the gun in slot `slot`, and what it buys (damage 1.12x a level, fire 6 % faster a level) */
export const gunLevelOf = (p: PlayerState, slot: number) => (slot === 0 ? p.wl0 : slot === 1 ? p.wl1 : 0);
export const gunLevelDmg = (p: PlayerState, slot: number) => 1 + 0.12 * gunLevelOf(p, slot);
export const gunLevelRate = (p: PlayerState, slot: number) => 1 - 0.06 * gunLevelOf(p, slot);

export function activeWeaponId(p: PlayerState): number {
  return p.wSlot === WSlot.Special ? p.w2 : p.wSlot === WSlot.Secondary ? p.w1 : p.w0;
}

/** Refill both loadout mags and the grenade belt (respawn, new loadout). The special keeps its count. */
export function refillLoadout(p: PlayerState) {
  if (p.wSlot === WSlot.Special && p.w2 === NO_WEAPON) p.wSlot = WSlot.Primary;
  p.mag0 = weaponDef(p.w0).mag; p.mag1 = weaponDef(p.w1).mag;
  p.mag = p.wSlot === WSlot.Special ? p.mag2 : p.wSlot === WSlot.Secondary ? p.mag1 : p.mag0;
  p.nades = GRENADE_MAX; p.chargeT = 0; p.burstN = 0;
}

function switchSlot(p: PlayerState, to: number) {
  if (to === p.wSlot) return false;
  if (to === WSlot.Special && p.w2 === NO_WEAPON) return false;
  if (p.wSlot === WSlot.Primary) p.mag0 = p.mag; else if (p.wSlot === WSlot.Secondary) p.mag1 = p.mag; else p.mag2 = p.mag;
  if (to !== WSlot.Special) p.prevSlot = to;
  else p.prevSlot = p.wSlot;
  p.wSlot = to;
  p.mag = to === WSlot.Primary ? p.mag0 : to === WSlot.Secondary ? p.mag1 : p.mag2;
  p.reloadT = 0; p.chargeT = 0; p.burstN = 0; p.spin = 0; p.steady = 0; p.bloom = 0;
  p.fireCd = Math.max(p.fireCd, Math.round(weaponOf(p).draw * 60));
  return true;
}

export function respawnAt(p: PlayerState, world: World, at: { x: number; y: number; z: number }) {
  const c = CLASSES[p.cls];
  const m = cardMods(p, MODS);
  p.x = at.x; p.y = at.y; p.z = at.z;
  p.vx = p.vy = p.vz = 0;
  p.hp = hullMaxOf(p, m); p.shield = shieldMaxOf(c.shieldMax, m); p.heat = 0; p.onFire = 0; p.flameT = 0;
  p.hitStunT = 0; p.knockT = 0; p.knockCd = 0;
  p.meleeHold = 0; p.reflectT = 0; p.pound = 0; p.dashN = VDASH_CHARGES; p.dashT = 0;
  refillLoadout(p); p.reloadT = 0; p.fireCd = 0;
  p.phase = Phase.Alive; p.phaseT = 0; p.reviveT = 0;
  p.lastGx = at.x; p.lastGy = at.y; p.lastGz = at.z;
  p.move = Move.Ground; p.grounded = 1;
  p.fallTopY = -1e9; p.stepY = at.y; // placed, not fallen
  p.iceN = 0; p.iceT = 0; p.gasT = 0; p.timeT = 0;
  p.iframes = secs(1.5);
}

export function clonePlayer(p: PlayerState): PlayerState {
  return { ...p };
}

export interface StepOut {
  shots: number; // how many shots fired this tick
  melee: boolean;
  dodge: boolean;
  special: boolean;
  reload: boolean;
  jump: boolean;
  land: number; // impact speed when landing
  fallDmg: number; // fall damage the game must apply (FLIGHT_DESIGN INVARIANT 2), after the heat cushion
  /** flew into a wall at this speed (BONK_MIN+): the game hurts and knocks the cowboy */
  bonk: number;
  /** the bonk's line of flight (unit, horizontal): which way he was going when he hit (sim/game.ts topples what he hit) */
  bonkX: number; bonkZ: number;
  /** a lighter bump: walked, sprinted, hovered or flew slowly into something solid at this speed (cosmetic: the client staggers the camera) */
  bump: number;
  /** punched into a house wall at flight speed (building index), or burst out of one (breachOut); -1 none (sim/destruct.ts BREACH) */
  breach: number; breachOut: number;
  launched: boolean;
  denied: boolean; // heat refused an action
  throw: boolean; // a grenade left the hand this tick
  cook: number; // ticks it was cooked before the throw
  cookOff: boolean; // held a fused grenade too long: it went off in the hand
  swap: boolean;
  shove: boolean; // first round racked into an empty gun (emptyShove weapons)
  drink: boolean; // uncorked a bottle this tick (sim/whiskey.ts)
  shieldBurnt: number; // shield a class move spent on itself this tick (Juggernaut Charge): it banks like a hit
  flameout: boolean; // the engine sputtered out on heat this tick
  blink: boolean; // Phantom: R3 held to BLINK_HOLD, the game finds the target and teleports him
  warpX: number; warpZ: number; warpSelf: number; // Phantom: the warp happened this tick; where it started, hot-pack self-damage
  hammer: number; // Juggernaut: the charged hammer came down with this charge (0..1); -1 none
  poundLand: boolean; // Valkyrie: the ground pound landed this tick (the game resolves it)
  skySlam: boolean; // Valkyrie with Downdraft: landed inside the window: the game slams and staggers around her
  // the shot's identity, read at the moment it fired (sim/weapons.ts Perk)
  mod: number; // ShotMod
  mul: number; // damage multiplier (sniper steady, partial plasma)
  spreadMul: number; // spread multiplier (Auto MG spin bloom, Big Iron fanning)
}
export const newStepOut = (): StepOut => ({ shots: 0, melee: false, dodge: false, special: false, reload: false, jump: false, land: 0, fallDmg: 0, bonk: 0, bonkX: 0, bonkZ: 0, bump: 0, breach: -1, breachOut: -1, launched: false, denied: false, throw: false, swap: false, shove: false, drink: false, shieldBurnt: 0, flameout: false, blink: false, warpX: 0, warpZ: 0, warpSelf: -1, hammer: -1, poundLand: false, skySlam: false, cook: 0, cookOff: false, mod: 0, mul: 1, spreadMul: 1 });
function resetOut(o: StepOut) {
  o.shots = 0; o.melee = o.dodge = o.special = o.reload = o.jump = o.launched = o.denied = o.throw = o.swap = o.shove = o.drink = o.cookOff = o.flameout = false; o.land = 0; o.fallDmg = 0; o.bonk = 0; o.bump = 0; o.breach = -1; o.breachOut = -1; o.cook = 0; o.shieldBurnt = 0; o.blink = false; o.warpSelf = -1; o.hammer = -1; o.poundLand = false; o.skySlam = false;
  o.mod = 0; o.mul = 1; o.spreadMul = 1;
}

// movement tuning (shared by all classes: top speed is class-invariant per FLIGHT_DESIGN)
// On foot: a jog that answers the stick in ~0.12 s and stops in ~0.1 s, a sprint on L3, a slow
// steady walk while aiming. Directional speed: backpedal and strafe are slower than forward, so
// the 8-way run clips read honestly.
/** playtest 3 (Nick): every cowboy movement runs a tenth slower; durations stretch to keep the distances (CONTROLS.md) */
export const PACE = 0.9;
const RUN_SPEED = 6.9 * PACE;
const SPRINT_SPEED = 9.8 * PACE;
const ADS_SPEED = 3.7;
const BACK_MUL = 0.78;
const STRAFE_MUL = 0.9;
const GROUND_ACCEL = 62;
const GROUND_TURN_ACCEL = 95; // reversing or cutting hard: extra bite so it never feels icy
const GROUND_FRICTION = 72;
const AIR_ACCEL = 20;
const AIR_SPEED = 6.9 * PACE; // = the jog: hopping must never beat running (airControl keeps momentum you already have)
const GRAVITY = 24;
const FALL_GRAVITY = 31; // heavier on the way down: snappier arcs, no floaty apex
const TERMINAL = 48;
const JUMP_V = 8.4;
const COYOTE = secs(0.1); // a jump still counts this long after running off a ledge
const JUMP_BUFFER = secs(0.15); // a press this early before landing jumps on touchdown
const IGNITE_HOLD = secs(0.2); // hold ✕ this long while airborne: traversal flight (FLIGHT_DESIGN §0.5c)
// traversal flight: the Godot flight model's numbers (scripts/player/flight_model.gd, FLIGHT_DESIGN §1-2).
// Top speed is class-invariant on purpose (BioWare shared it so squads travel together); classes differ in
// thrust (mass), turn rate and heat. Level 14 m/s, a full dive 2x that, a full climb 0.55x.
const FLIGHT_SPEED = 14 * PACE; // V_LEVEL
const FLIGHT_GLIDE = 3; // stick released: Godot targets 0 and coasts down at A_DECEL; a hair of way on keeps the steering alive
const FLIGHT_DIVE_SPEED = 28; // V_LEVEL x DIVE_MUL 2.0
const FLIGHT_CLIMB_MUL = 0.55; // V_LEVEL x CLIMB_MUL straight up
const ACCEL_EASE_REF = 5; // m/s of error over which thrust eases out (an S-curve, not a corner at the top)
const DRAG_K = 1.2; // thrust is divided by (1 + DRAG_K (v/V_LEVEL)^2): a dive takes longer to reach than a cruise
const A_DECEL = 30.5 / 5.2; // Godot A_DECEL 5.2 for the Gunslinger: decel = thrust / this
const DECEL_EASE_REF = 10;
const DECEL_THROTTLE_GATE = 0.15; // with the stick still held only this much of the braking survives (a pull-out keeps its speed)
const OVERSPEED_DRAG = 0.006; // x v^2, always
// heat (scripts/player/flight_heat.gd rate_for): one rate, cruise + climb arm - dive arm. A dive drains hard,
// never enough to buy its altitude back (FLIGHT_DESIGN INVARIANT 1, kept by the move test's sawtooth).
const CLIMB_SHAPE = 1.5; // PITCH_SHAPE
const DIVE_REF = 0.73, DIVE_NEAR = 29.5, DIVE_FAR = 34, DIVE_SHAPE = 1.6; // the two-slope dive arm, x the class's heat build
const IGNITE_V = 9.5;
const IGNITE_HEAT = 5;
const LAUNCH_V = 13.5; // ground launch into flight
const LAUNCH_HEAT = 6;
// hover (the firing platform): stall from flight, pop up from the ground
const HOVER_DRIFT = 5.2;
const HOVER_ACCEL = 18;
const HOVER_STALL = secs(0.38);
const HOVER_POP_V = 8.5;
const HOVER_TRIM = 3.2; // m/s of altitude trim from the look pitch past the deadzone
const HOVER_TRIM_DZ = 0.45;
// defensive verbs: the Godot game's (scripts/player/player.gd "Dodge" / "Slide" / "Dive"; Nick 2026-10-21, James approved)
const ROLL_TICKS = secs(0.9 / PACE); // the roll: 8.1 m/s for 1.0 s = 8.1 m (9 m/s for 0.9 s before the pace), keeping any faster momentum up to ROLL_CARRY x
const ROLL_SPEED = 9 * PACE;
const ROLL_CARRY = 1.45;
const ROLL_IFRAMES = secs(0.75 / PACE); // ~83 % of the roll is safe; the tail is the get-up, and the cost
const ROLL_CD = secs(0.5);
const ROLL_CANCEL = secs(0.4); // after this the roll can be cancelled into a jump (a web keeper)
const SLIDE_TICKS = secs(0.7 / PACE); // ○ out of a sprint: 16 m/s easing to 30 % over 0.7 s (before the pace)
const SLIDE_SPEED = 16 * PACE;
const SLIDE_END = 0.3;
const SLIDE_CD = secs(1.0);
// the air dive (Helldivers-style): ○ airborne throws you along the stick (forward on neutral) at your own speed
// between DIVE_MIN and DIVE_MAX, rise capped at DIVE_UP, safe for DIVE_IFRAMES; you land prone and slide out
// under PRONE_FRICTION (at least PRONE_MIN, at most PRONE_MAX), then take PRONE_GETUP to get up, doing nothing
const DIVE_MIN = 11 * PACE;
const DIVE_MAX = 16 * PACE;
const DIVE_UP = 2;
const DIVE_IFRAMES = secs(0.6);
const DIVE_AIR_MAX = secs(2.5); // a dive off a cliff ends in a plain fall after this
const DIVE_MIN_AIR = secs(0.15); // a stair blip isn't airborne; a real jump's rise (> DIVE_MIN_RISE) always is
const DIVE_MIN_RISE = 3;
const DIVE_CD = secs(1.0);
const PRONE_FRICTION = 24;
const PRONE_MIN = secs(0.25);
const PRONE_MAX = secs(0.8);
const PRONE_GETUP = secs(0.5);
// the hover dodge (the web's: a flip toward the stick out of a hover, paid for by the hover's heat)
const HOVER_DASH_TICKS = secs(0.26 / PACE);
const HOVER_DASH_SPEED = 15 * PACE;
const BARREL_TICKS = secs(0.4);
const BARREL_SIDE = 14; // Godot 16/sqrt(mass) eased over 0.35 s; x sqrt(thrust / 30.5) per class below
const BARREL_IFRAMES = secs(0.4);
const DODGE_CD = secs(0.2); // after a hover dodge ends
// landings
const HARD_LAND_V = 20; // falling faster than this (a ~6.5 m drop): the superhero landing
const HERO_FLIGHT_SINK = 12; // or a fast flight landing sinking at least this hard
const DODGE_BUFFER = secs(0.15); // a ○ pressed while a dodge can't start fires as soon as it can
const HERO_LAND_TICKS = secs(0.62);
const HERO_LOCK = secs(0.2); // fully planted; jump / dodge fire as it ends
const SOFT_LAND_TICKS = secs(0.14);
const KILL_Y = -60;
// water (sim/world.ts): the cooling highway. Wading, skimming low over it, or flying through a
// waterfall dumps heat; skimming beats any flight heat cost, so a stream is a line of free flight.
// It costs exposure: you're low, in the open, on the robots' terms (FLIGHT_DESIGN §3.1b).
const WADE_COOL = 30; // Godot: the kit's soak and the wet zone together, ~17 /s each
const SKIM_COOL = 95; // Godot K_SKIM at the surface, falling off to 0 at WATER.skimH
const FALL_COOL = 140;
const WADE_MUL = 0.82;
/** HP/s while a burning building's fire is on you (sim/destruct.ts); the pack's own fire is HEAT_FIRE_DPS */
const BUILDING_FIRE_DPS = 7;

// fall damage (FLIGHT_DESIGN §3.1c, the Godot build's measured numbers)
/** Optional rules James hasn't switched on yet. Server and client build from this one file, so flipping a value
 * here changes it for everyone. fallDamage: FLIGHT_DESIGN INVARIANT 2 (falls past 9 m hurt, a cool pack cushions). */
export const RULES = { fallDamage: false, /** the extraction chase back across the village (CHASE.md); off in a few old tests */ chase: true };
const FALL_FREE_M = 9; // 2x the highest non-flight apex: nothing short of a real fall hurts
const FALL_DAMAGE_PER_M = 1.4; // 25 m -> 22 HP, 80 m -> lethal
const CUSHION_HP_PER_HEAT = 0.55; // a cold pack eats about half a health bar
const CUSHION_HEAT_CEIL = 0.78; // spend up to here (above 0.8 the pack catches fire)

/** which way a barrel roll jukes, as seen from behind the flyer: 1 right, -1 left (the dodge vector against the travel) */
export const barrelSide = (p: { vx: number; vz: number; dodgeX: number; dodgeZ: number }) => (p.dodgeZ * p.vx - p.dodgeX * p.vz < 0 ? -1 : 1);
/** dodge kinds (p.dodgeK): Roll is also the slide (p.slide); Dive is the air dive (it lands prone: LandK.Prone) */
export const enum DodgeK { Roll = 0, Dive = 1, Barrel = 2, HoverDash = 3, /** the Phantom's ground dodge: a straight dash, never a tumble (on the wire it reads as Roll: the class tells it apart) */ Dash = 4 }
const DASH_TICKS = secs(0.32 / PACE), DASH_SPEED = 21 * PACE;
/** flying into a wall or a building (Nick, playtest 2): past this speed it hurts and knocks you out of flight */
export const BONK_MIN = 9, BONK_DMG_PER_MS = 2.2, BONK_MAX = 45;
/** a softer knock (walking, sprinting, hovering into a wall) past this is felt: the camera jolts, he grunts (no damage) */
export const BUMP_MIN = 3.2;
/** landing kinds (p.landK): Prone is the dive's belly slide and get-up */
export const enum LandK { None = 0, Soft = 1, Hero = 2, Prone = 3 }

const NO_SOLIDS: readonly Solid[] = [];
/** the stepping cowboy's hand aggregate (sim/cards.ts), filled at the top of stepPlayer and read by the helpers below */
const MODS: Mods = newMods();
export const modsOf = (p: PlayerState) => cardMods(p, MODS);
/** Super Coolant halves every heat cost while it runs */
const heatMul = (p: PlayerState) => (p.puAct === PU.SuperCoolant ? COOLANT_MUL : 1);
function canSpend(p: PlayerState, cost: number) {
  return p.heat + cost * heatMul(p) < CLASSES[p.cls].heatMax;
}
function addHeat(p: PlayerState, amount: number) {
  if (amount <= 0) return;
  amount *= heatMul(p) * MODS.heatGainMul; // Pressure Cooker and friends
  p.heat = Math.min(CLASSES[p.cls].heatMax, p.heat + amount);
  p.heatGrace = Math.max(p.heatGrace, secs(CLASSES[p.cls].coolDelay));
}
/** the engine sputters out: no jets for the dead time, then a hard vent until the pack relights at RELIGHT */
function flameout(p: PlayerState, out: StepOut) {
  if (p.flameT === 0) { p.flameT = FLAMEOUT_DELAY; out.flameout = true; }
}
/** reload heat with the hand on: Frenzy doubles it, Cold Streak frees it while a chain is open */
function reloadHeatMul(p: PlayerState, m: Mods) { return (p.chainT > 0 && m.chainFreeHeat ? 0 : 1) * m.reloadHeatMul; }
/** the dive arm of the heat curve: gross heat/s vented at nose-down deflection d (0..1), before the class multiplier */
function diveDrain(d: number) {
  return d <= DIVE_REF ? DIVE_NEAR * Math.pow(d / DIVE_REF, DIVE_SHAPE) : DIVE_NEAR + (DIVE_FAR - DIVE_NEAR) * (d - DIVE_REF) / (1 - DIVE_REF);
}

/** something a cowboy can't walk through this tick (a robot): a vertical cylinder, feet at y0, top at y1 */
export interface Solid { x: number; z: number; y0: number; y1: number; r: number }
/** robots a cowboy walks into on foot or in the air stop him (Godot: kinematic bodies block each other). A dash
 *  or warp (Move.Special) passes through, so the Valkyrie's fire dash and the Phantom's warp still thread a crowd
 *  and the Juggernaut's charge bowls through it. The server resolves against where the cowboy SAW the robots
 *  (lag-compensated history), the client against its interpolated view, so prediction agrees. */
export function pushOutOfSolids(p: PlayerState, solids: readonly Solid[]) {
  if (p.move === Move.Special || p.paused || p.skyT > 0) return;
  const top = p.y + PLAYER_HEIGHT;
  for (const s of solids) {
    if (p.y >= s.y1 - 0.05 || top <= s.y0) continue;
    const dx = p.x - s.x, dz = p.z - s.z;
    const rr = s.r + PLAYER_RADIUS;
    const d2 = dx * dx + dz * dz;
    if (d2 >= rr * rr) continue;
    const d = Math.sqrt(d2);
    if (d < 1e-4) { p.x = s.x + rr; continue; } // dead centre: push out along +x
    const push = (rr - d) / d;
    p.x += dx * push; p.z += dz * push;
    // kill the velocity into it, keep the slide along it
    const nx = dx / d, nz = dz / d;
    const into = p.vx * nx + p.vz * nz;
    if (into < 0) { p.vx -= into * nx; p.vz -= into * nz; }
  }
}

/**
 * Advance one cowboy by one tick. Pure function of (p, input, world, solids): the server and the
 * owning client produce the same result, so prediction never waits on the network.
 */
export function stepPlayer(p: PlayerState, inp: InputFrame, world: World, out: StepOut, solids: readonly Solid[] = NO_SOLIDS): StepOut {
  resetOut(out);
  const c = CLASSES[p.cls];
  const m = cardMods(p, MODS);
  const shieldCap = shieldMaxOf(c.shieldMax, m);
  const lowHeat = m.lowHeatFloor > 0 && p.heat < c.heatMax * m.lowHeatFloor;
  if (p.quickVentT > 0) p.quickVentT--;
  let b = inp.buttons;
  let pressed = b & ~p.prevButtons;
  let released = p.prevButtons & ~b;
  p.prevButtons = b;
  p.yaw = wrapAngle(inp.yaw);
  p.pitch = clamp(inp.pitch, -1.45, 1.45);

  // ---- pause: lift to the sky, safe; come back down somewhere safe, vulnerable
  const wantPause = (b & Btn.Pause) !== 0 && p.phase !== Phase.Dead;
  if (wantPause && !p.paused) {
    p.paused = 1;
    p.vx = p.vy = p.vz = 0;
    p.move = Move.Air;
  } else if (!wantPause && p.paused && p.skyT >= 1) {
    p.paused = 0;
    // descend onto the last safe ground, never the exact pause spot
    p.x = p.lastGx + 1.5; p.z = p.lastGz + 1.5;
    p.y = world.support(p.x, p.z, p.lastGy + 2) + 0.05;
    if (p.y < -1e6) { p.x = p.lastGx; p.z = p.lastGz; p.y = p.lastGy; }
  }
  // a downed cowboy's bleed-out runs on through the sky trip (pausing keeps you safe, not alive)
  if ((p.paused || p.skyT > 0) && p.phase === Phase.Down && p.phaseT > 0) p.phaseT--;
  if (p.paused) {
    p.skyT = Math.min(1, p.skyT + DT / 1.0);
    return out;
  }
  if (p.skyT > 0) {
    p.skyT = Math.max(0, p.skyT - DT / 1.5);
    if (p.skyT === 0) { p.iframes = secs(0.4); p.grounded = 1; p.move = Move.Ground; }
    return out;
  }

  if (p.iframes > 0) p.iframes--;
  if (p.fireCd > 0) p.fireCd--;
  if (p.throwCd > 0) p.throwCd--;
  if (p.meleeCd > 0) p.meleeCd--;
  if (p.meleeT > 0) p.meleeT--;
  if (p.dodgeCd > 0) p.dodgeCd--;
  if (p.specialCd > 0) p.specialCd--;
  // the chain window; Icarus Pact holds it open while airborne; Overdrive torches the pack when it closes
  if (p.chainT > 0) {
    if (!(m.skyIcarus && p.cls === ClassId.Valkyrie && !p.grounded && p.chainT === 1)) p.chainT--;
    if (p.chainT === 0) { if (p.chainN > 0 && m.chainEndOverheat) p.heat = c.heatMax; p.chainN = 0; p.chainMissUsed = 0; }
  }
  if (p.cloakT > 0) p.cloakT--;
  if (p.reflectT > 0) p.reflectT--;
  if (p.acidT > 0) p.acidT--;
  if (p.acidBoostT > 0) p.acidBoostT--;
  if (p.burnT > 0) { p.burnT--; if (p.phase === Phase.Alive) p.hp -= PLAYER_BURN_DPS * DT; } // an element's fire: through the shield
  // the Valkyrie's dash charges come back one every VDASH_RECHARGE
  if (p.dashN < VDASH_CHARGES && ++p.dashT >= VDASH_RECHARGE) { p.dashN++; p.dashT = 0; }
  // ice stacks bleed off one at a time; gas slow lasts while the server keeps refreshing it
  if (p.iceT > 0 && --p.iceT === 0 && p.iceN > 0) { p.iceN--; if (p.iceN > 0) p.iceT = secs(ICE_STACK_DECAY_SECS); }
  if (p.gasT > 0) p.gasT--;
  if (p.timeT > 0) p.timeT--;
  let slow = (1 - ICE_SLOW_PER_STACK * Math.min(ICE_SLOW_MAX_STACKS, p.iceN)) * (p.gasT > 0 ? GAS_SLOW_MUL : 1) * (p.timeT > 0 ? TIME_SLOW_MUL : 1);

  // ---- heat economy: reload, jets and specials cost heat; time and diving cool it
  if (p.heatGrace > 0) p.heatGrace--;
  else p.heat = Math.max(0, p.heat - c.heatCool * m.coolMul * DT);
  // the flameout: dead time, then the pack vents hard until it relights (flight_heat.gd RECOVERY_DELAY / VENT_RATE / RELIGHT)
  if (p.flameT > 0) { if (--p.flameT === 0) p.flameT = -1; }
  else if (p.flameT < 0) {
    p.heat = Math.max(0, p.heat - FLAMEOUT_VENT * DT);
    if (p.heat <= c.heatMax * RELIGHT) p.flameT = 0;
  }
  const frac = p.heat / c.heatMax;
  if (p.phase === Phase.Alive) {
    // Fire Power: the pack can't burn you while it runs (powerups.ts)
    const fireproof = p.puAct === PU.ElementalPower && p.puActEl === PU_FIRE_EL;
    if (p.heat >= c.heatMax - 0.01 && !fireproof && !p.onFire) p.onFire = 1;
    // a burning building sets you alight (sim/destruct.ts). onFire counts the ticks that fire has
    // left (> 1); a pack fire is 1 and goes out once the pack cools below the red band. A roll or water puts out either.
    if (!fireproof && p.paused === 0 && world.dest.fireTouch(p.x, p.y, p.z, PLAYER_RADIUS, PLAYER_HEIGHT)) p.onFire = Math.max(p.onFire, BUILDING_FIRE_TICKS);
    // Overcook Insurance: a pack fire puts itself out after 3 s
    if (p.onFire === 1) { p.fireT++; if (m.overcook && p.fireT >= secs(3)) { p.onFire = 0; p.heat = Math.min(p.heat, c.heatMax * (HEAT_RED_FRAC - 0.02)); } } else p.fireT = 0;
    if (p.onFire) {
      // a pack fire is the heavy burn (GAMEPLAY_LOOP §1.5: catch fire + lockout): it bypasses the shield and keeps
      // the pack hot. A building's fire on your clothes burns slower and times out on its own.
      p.hp -= (p.onFire === 1 ? HEAT_FIRE_DPS : BUILDING_FIRE_DPS) * DT;
      if (p.onFire === 1 && !fireproof) p.heat = Math.min(c.heatMax, p.heat + HEAT_FIRE_SELF * DT);
      if (p.onFire > 1) p.onFire--;
      else if (frac < HEAT_RED) p.onFire = 0;
    } else if (frac >= HEAT_RED && !fireproof) p.hp -= HEAT_RED_DPS * DT; // the mild burn: the edge zone you can tank for a moment
    if (p.hp <= 0) { p.hp = 0; p.phase = Phase.Down; p.phaseT = secs(30); }
  }
  // ---- shield regen: after the delay, and every point costs heat (regen pauses rather than push the pack into the red)
  if (p.shieldDelay > 0) p.shieldDelay--;
  else if (p.shield < shieldCap) {
    const amount = Math.min(shieldCap - p.shield, c.shieldRegen * m.regenMul * (p.quickVentT > 0 ? 2 : 1) * DT);
    const cost = amount * SHIELD_REGEN_HEAT * heatMul(p);
    if (p.heat + cost < c.heatMax * SHIELD_REGEN_HEAT_CEIL) {
      p.heat += cost; // a passive trickle: it doesn't reset the cooling grace, so cooling and regen run in parallel
      p.shield += amount;
    }
  }

  const alive = p.phase === Phase.Alive;
  if (p.phase === Phase.Down && p.phaseT > 0) p.phaseT--;

  // ---- weight: the hit stun freezes input while the shove plays out (the Phantom's warp stays usable as the
  // emergency exit, as in Godot); a knockdown is the stun writ large: flat on the ground, then an invulnerable get-up
  if (p.knockCd > 0) p.knockCd--;
  if (p.hitStunT > 0) p.hitStunT--;
  const floored = p.knockT > GETUP;
  if (p.knockT > 0) {
    p.knockT--;
    if (p.knockT === GETUP) p.iframes = Math.max(p.iframes, GETUP);
  }
  const stunned = p.hitStunT > 0 || floored || (p.cls === ClassId.Juggernaut && p.meleeHold >= HAMMER_WINDUP);
  if (stunned) {
    const keep = Btn.Pause | (p.cls === ClassId.Phantom && !floored ? Btn.Special : 0) | (p.cls === ClassId.Juggernaut && p.meleeHold > 0 && !floored ? Btn.Melee : 0);
    b &= keep; pressed &= keep; released &= keep;
    inp = { ...inp, mx: 0, my: 0 };
    p.sprint = 0;
    if (floored) { p.reloadT = 0; p.chargeT = 0; p.burstN = 0; p.blade = 0; p.combo = 0; p.comboT = 0; p.meleeBuf = 0; p.jumpBuf = 0; p.dodgeBuf = 0; }
  }
  // ---- whiskey (sim/whiskey.ts): uncork, a second of chugging, then the heal and the adrenaline
  if (!alive) p.drinkT = 0;
  if (p.stimT > 0) p.stimT--;
  if (p.drinkT > 0) { if (--p.drinkT === 0) p.stimT = HEAL_TICKS; }
  else if (alive && (pressed & Btn.Drink) && p.bottles > 0 && p.stimT === 0 && p.meleeT === 0 && p.cookT === 0
    && p.move !== Move.Flight && p.move !== Move.Dodge && p.move !== Move.Special) {
    p.bottles--;
    p.drinkT = CHUG_TICKS;
    p.reloadT = 0; p.sprint = 0; p.chargeT = 0; p.burstN = 0;
    p.blade = 0; p.combo = 0; p.comboT = 0; p.meleeBuf = 0; p.jumpBuf = 0; p.dodgeBuf = 0;
    out.drink = true;
  }
  if (p.drinkT > 0) { b &= CHUG_KEEP; pressed &= CHUG_KEEP; released &= CHUG_KEEP; slow *= CHUG_WALK_MUL; }



  // ---- intent in world space: the stick steers along the CAMERA, never the parallax-corrected aim line
  // (the aim yaw jumps by up to ~14 degrees as the crosshair crosses near objects; movement must not)
  const myaw = inp.myaw;
  const sy = Math.sin(myaw), cy = Math.cos(myaw);
  let wx = -sy * inp.my + cy * inp.mx;
  let wz = -cy * inp.my - sy * inp.mx;
  let wl = len2(wx, wz);
  if (wl > 1) { wx /= wl; wz /= wl; wl = 1; }
  const moving = wl > 0.1;
  if (moving || !p.grounded) p.stillT = 0; else p.stillT = Math.min(p.stillT + 1, 600);
  const aimHeld = (b & Btn.Aim) !== 0 && alive;
  p.aimOn = aimHeld ? 1 : 0;
  const jumpHeld = (b & Btn.Jump) !== 0 && alive;
  const onGround = p.grounded === 1;
  if (p.landT > 0) { p.landT--; if (p.landT === 0) { if (p.landK === LandK.Prone) p.dodgeCd = DIVE_CD; p.landK = LandK.None; if (p.move === Move.Land) p.move = Move.Ground; } }
  const proneLocked = p.landK === LandK.Prone && p.landT > 0;
  const heroLocked = (p.landK === LandK.Hero && p.landT > HERO_LAND_TICKS - HERO_LOCK) || proneLocked;
  // input buffers age at the top of the step (a press and its first decrement used to share a tick, which
  // cut the window short), and hold while planted so a press during the hero landing fires as it lets go
  if (!heroLocked) { if (p.jumpBuf > 0) p.jumpBuf--; if (p.dodgeBuf > 0) p.dodgeBuf--; }

  // ---- sprint: L3 latches it; it ends when you stop, aim, turn back or fire
  if (alive && (pressed & Btn.Sprint)) p.sprint = p.sprint ? 0 : 1;
  if (p.sprint) {
    const fwd = wl > 0.01 ? inp.my / wl : 0; // forward share of the stick
    if (!moving || aimHeld || fwd < 0.35 || p.phase !== Phase.Alive || (b & Btn.Fire) || p.reloadT > 0) p.sprint = 0;
  }

  // ---- defensive verbs (○): roll on foot (a slide out of a sprint), the air dive when airborne, a flip out of a
  // hover, a barrel roll in flight. A dive needs real air under you (DIVE_MIN_AIR, or a jump's rise)
  const diveOk = onGround || p.hoverMode === 1 || p.move === Move.Flight || p.airT >= DIVE_MIN_AIR || p.vy > DIVE_MIN_RISE;
  const dodgeReady = p.dodgeCd === 0 && p.move !== Move.Special && p.move !== Move.Dodge && !(p.move === Move.Flight && p.dodgeT > 0) && diveOk && !proneLocked;
  if (alive && (pressed & Btn.Dodge)) p.dodgeBuf = DODGE_BUFFER;
  if (alive && p.dodgeBuf > 0 && dodgeReady && !(p.landK === LandK.Hero && p.landT > HERO_LAND_TICKS - secs(0.1))) {
    p.dodgeBuf = 0;
    let dx = wx, dz = wz;
    if (!moving) { dx = sy; dz = cy; } // no stick: hop back out of trouble
    const l = len2(dx, dz) || 1;
    p.dodgeX = dx / l; p.dodgeZ = dz / l;
    p.slide = 0;
    if (p.move === Move.Flight) {
      p.dodgeK = DodgeK.Barrel; p.dodgeT = BARREL_TICKS;
      const side = inp.mx < -0.2 ? -1 : 1; // juke toward the stick, right by default
      p.dodgeX = cy * side; p.dodgeZ = -sy * side;
      p.iframes = Math.max(p.iframes, BARREL_IFRAMES);
    } else if (!onGround && p.hoverMode) {
      p.dodgeK = DodgeK.HoverDash; p.dodgeT = HOVER_DASH_TICKS;
      p.iframes = Math.max(p.iframes, secs(0.22));
    } else if (!onGround) {
      // the air dive: along the stick, forward on neutral (a face-first backward dive would be nonsense)
      if (!moving) { p.dodgeX = -sy; p.dodgeZ = -cy; }
      p.dodgeK = DodgeK.Dive; p.dodgeT = DIVE_AIR_MAX;
      const sp = clamp(len2(p.vx, p.vz), DIVE_MIN, DIVE_MAX);
      p.vx = p.dodgeX * sp; p.vz = p.dodgeZ * sp; p.vy = Math.min(p.vy, DIVE_UP);
      p.iframes = Math.max(p.iframes, DIVE_IFRAMES);
    } else if (p.sprint && moving) {
      // the slide: ○ out of a sprint keeps you low and fast along your line, no i-frames
      p.dodgeK = DodgeK.Roll; p.slide = 1; p.dodgeT = SLIDE_TICKS; p.rollV = SLIDE_SPEED;
      p.landT = 0; p.landK = LandK.None;
    } else if (p.cls === ClassId.Phantom) {
      // the Phantom dashes (Nick, playtest 2): a flat burst along the stick, safe for the whole of it, a ghost left behind
      p.dodgeK = DodgeK.Dash; p.dodgeT = DASH_TICKS; p.rollV = DASH_SPEED;
      p.iframes = Math.max(p.iframes, DASH_TICKS);
      p.landT = 0; p.landK = LandK.None;
    } else {
      p.dodgeK = DodgeK.Roll; p.dodgeT = ROLL_TICKS;
      p.rollV = clamp(len2(p.vx, p.vz), ROLL_SPEED, ROLL_SPEED * ROLL_CARRY);
      p.iframes = Math.max(p.iframes, ROLL_IFRAMES);
      p.landT = 0; p.landK = LandK.None;
    }
    p.dodgeCd = 0;
    if (p.dodgeK !== DodgeK.Barrel) p.move = Move.Dodge;
    if (p.onFire) { p.onFire = 0; p.heat = Math.min(p.heat, c.heatMax * (HEAT_RED - 0.02)); } // stop, drop, roll: out of the red band
    if (m.dodgeVent > 0) p.heat = Math.max(0, p.heat - p.heat * m.dodgeVent); // Cooldown Vent
    if (p.reloadT > 0 && weaponOf(p).rounds) p.reloadT = 0;
    out.dodge = true;
  }
  const dashOk = p.cls !== ClassId.Valkyrie || p.dashN > 0; // her dash is a charge, not a cooldown
  if (alive && (pressed & Btn.Special) && c.abilityCd > 0 && p.specialCd === 0 && !(p.move === Move.Flight && p.dodgeT > 0) && !proneLocked && !p.pound) {
    if (!p.onFire && canSpend(p, c.abilityHeat) && dashOk) {
      // ○○: the second tap turns the roll just started into the class dash (Godot's enhanced dash)
      if (p.move === Move.Dodge) { p.dodgeT = 0; p.slide = 0; }
      const heat0 = p.heat / c.heatMax;
      addHeat(p, c.abilityHeat);
      p.specialCd = p.cls === ClassId.Valkyrie ? VDASH_GAP : c.abilityCd;
      p.specialT = p.cls === ClassId.Phantom ? 1 : p.cls === ClassId.Valkyrie ? VDASH_TICKS : secs(0.5);
      p.iframes = Math.max(p.iframes, p.cls === ClassId.Phantom ? WARP_IFRAMES : p.cls === ClassId.Valkyrie ? VDASH_IFRAMES : p.specialT);
      if (p.cls === ClassId.Valkyrie) { p.dashN--; if (p.dashT === 0) p.dashT = 1; }
      if (p.cls === ClassId.Phantom) {
        // the warp: a true teleport WARP_DIST along the camera, pulled back from walls and edges, on a hot pack it
        // burns you (and may light the pack); the sweep through robots is the game's (out.warpX/Z)
        out.warpX = p.x; out.warpZ = p.z;
        const fx = -Math.sin(inp.myaw), fz = -Math.cos(inp.myaw);
        let placed = false;
        for (let d = WARP_DIST; d >= 1.5 && !placed; d -= 0.75) {
          const q = { x: p.x + fx * d, y: p.y, z: p.z + fz * d };
          const g = world.support(q.x, q.z, p.y + 3);
          if (g === NO_GROUND || g < p.y - 4 || g > p.y + 2.5) continue;
          q.y = g;
          world.pushOut(q, PLAYER_RADIUS, PLAYER_HEIGHT);
          if (world.support(q.x, q.z, q.y + 1) === NO_GROUND) continue;
          p.x = q.x; p.z = q.z; p.y = Math.max(q.y, world.support(q.x, q.z, q.y + 1));
          placed = true;
        }
        p.vx = p.vz = 0; p.vy = Math.max(0, p.vy);
        p.grounded = 1; p.hoverMode = 0; p.stallT = 0;
        p.reflectT = WARP_REFLECT;
        for (const [at, dmg] of WARP_SELF_DMG) if (heat0 >= at) { out.warpSelf = dmg; break; }
        if (p.heat >= c.heatMax * HEAT_RED_FRAC) p.onFire = 1; // a hot warp lights the pack
      }
      // Shield Charge: the whole shield goes into the dash (and banks into the special and grenades, game.ts);
      // self-spent shield restarts the regen clock like a hit
      if (p.cls === ClassId.Juggernaut) {
        out.shieldBurnt = m.jugFull ? shieldCap : p.shield; p.shield = 0; p.shieldDelay = Math.max(p.shieldDelay, shieldDelayFor(p.cls));
        if (m.jugVent > 0) p.heat = Math.max(0, p.heat - c.heatMax * m.jugVent); // Overcapacitor
        if (m.jugDashHp > 0) p.hp = Math.max(1, p.hp - m.jugDashHp); // Overdraft's price
        if (m.jugQuickVent) p.quickVentT = secs(6);
      }
      // the warp leaves you in the smoke: robots lose you and your next strike is a backstab from any angle
      if (p.cls === ClassId.Phantom) p.cloakT = Math.max(p.cloakT, WARP_CLOAK);
      p.move = Move.Special;
      p.hoverMode = 0;
      out.special = true;
    } else out.denied = true;
  }

  // ---- the flight grammar (Anthem's, simplified to one rule per button; CONTROLS.md):
  //   ✕ is the engine: jump on the ground; in the air, press it again to FLY (or keep it held after the jump);
  //     press it in flight to cut the engine and fall; press it in a hover to take off into flight.
  //   L1 is the hover: in the air, L1 stops you in a hover (from a jump, a fall or flight); L1 again drops out.
  //     On the ground it pops you up into one. Aiming or firing in flight also stalls into a hover.
  const flying = p.move === Move.Flight;
  const canJet = alive && !p.onFire && p.flameT === 0;
  if (!onGround) { if (jumpHeld) { if (p.jumpHold <= IGNITE_HOLD) p.jumpHold++; } else p.jumpHold = 0; } else p.jumpHold = 0;
  // the hold counts from take-off, so a held jump becomes flight 0.2 s after leaving the ground
  const holdIgnite = p.jumpHold === IGNITE_HOLD;
  // a press in the air: not the coyote jump off a ledge, and not a jump buffered just before touchdown
  let airPress = false;
  if (!onGround && (pressed & Btn.Jump) && alive && !(p.coyote > 0 && p.vy <= 0)) {
    const nearFloor = p.vy < 0 && p.y - world.support(p.x, p.z, p.y) < 0.9;
    airPress = !nearFloor;
    if (airPress) p.jumpHold = IGNITE_HOLD + 1; // this press is its own verb: holding it on doesn't re-ignite
  }
  const hoverPress = (pressed & Btn.Hover) !== 0 && alive;
  p.jumpTap = 0;

  if (!onGround && p.move !== Move.Dodge && p.move !== Move.Special) {
    if (flying) {
      if (airPress || !canJet || !canSpend(p, c.flightHeat * DT)) {
        // cut the engine: momentum carries, the nose drops
        p.move = Move.Air; p.vx *= 0.8; p.vz *= 0.8; p.vy = Math.min(p.vy, p.vy * 0.6);
        if (!canJet || !canSpend(p, c.flightHeat * DT)) { out.denied = true; flameout(p, out); }
      } else if (hoverPress || (pressed & (Btn.Aim | Btn.Fire))) {
        // the stall: nose up against the travel, speed dumps, guns come out (aiming or firing teaches it)
        p.move = Move.Hover; p.hoverMode = 1; p.stallT = HOVER_STALL; p.hoverY = p.y;
      }
    } else if (p.hoverMode) {
      if (hoverPress) { p.hoverMode = 0; p.move = Move.Air; p.stallT = 0; } // drop out
      else if (airPress && canJet && canSpend(p, IGNITE_HEAT)) {
        // take off out of the hover where you're looking: eager, faster than the stall
        p.hoverMode = 0; p.move = Move.Flight; p.stallT = 0; addHeat(p, IGNITE_HEAT);
        const cp = Math.cos(inp.pitch);
        const s = 14;
        p.vx = -Math.sin(inp.yaw) * cp * s; p.vz = -Math.cos(inp.yaw) * cp * s; p.vy = Math.max(3, Math.sin(inp.pitch) * s);
        out.launched = true;
      } else if (airPress) out.denied = true;
      else if (!canJet || !canSpend(p, c.hoverHeat * DT)) { p.hoverMode = 0; p.move = Move.Air; p.stallT = 0; out.denied = true; flameout(p, out); }
    } else {
      if ((airPress || holdIgnite || (pressed & Btn.Fly)) && p.hitStunT === 0) { // (a bonk's stun keeps the pack off: you fall)
        if (canJet && canSpend(p, IGNITE_HEAT)) {
          addHeat(p, IGNITE_HEAT);
          p.move = Move.Flight;
          p.vy = Math.max(p.vy, IGNITE_V);
          // take off toward the aim (Anthem's default), keeping any faster momentum you already had
          const hs = Math.max(10, len2(p.vx, p.vz));
          p.vx = -Math.sin(inp.yaw) * hs; p.vz = -Math.cos(inp.yaw) * hs;
          out.launched = true;
        } else out.denied = true;
      } else if (hoverPress) {
        if (canJet && canSpend(p, c.hoverHeat * 0.3)) { p.hoverMode = 1; p.move = Move.Hover; p.stallT = secs(0.2); p.hoverY = p.y + Math.max(0, p.vy) * 0.2; }
        else out.denied = true;
      }
    }
  }

  // ---- movement modes
  const crawl = p.phase === Phase.Down ? 0.2 : 1;
  const waterY = world.waterAt(p.x, p.z);
  const wading = onGround && waterY !== NO_GROUND && waterY - p.y > 0.45;
  if (p.move === Move.Dodge) {
    p.dodgeT--;
    if (p.dodgeK === DodgeK.Roll && p.slide) {
      // the slide: fast off the sprint, easing to a walk by the end; it keeps the sprint latched
      const t = 1 - p.dodgeT / SLIDE_TICKS;
      const s = SLIDE_SPEED * (1 - (1 - SLIDE_END) * t);
      p.sprint = 1;
      if (moving) { const k = 0.04; p.dodgeX += (wx - p.dodgeX) * k; p.dodgeZ += (wz - p.dodgeZ) * k; const l = len2(p.dodgeX, p.dodgeZ) || 1; p.dodgeX /= l; p.dodgeZ /= l; }
      p.vx = p.dodgeX * s; p.vz = p.dodgeZ * s;
      if (!onGround) p.vy -= GRAVITY * DT;
    } else if (p.dodgeK === DodgeK.Dive) {
      // airborne: the launch line holds, gravity rules; touching down starts the prone slide
      p.vy -= (p.vy > 0 ? GRAVITY : FALL_GRAVITY) * DT;
      if (p.vy < -TERMINAL) p.vy = -TERMINAL;
      if (onGround) {
        p.dodgeT = 0;
        p.landK = LandK.Prone; p.landT = PRONE_MAX + PRONE_GETUP; p.move = Move.Land;
        p.sprint = 0; p.iframes = Math.max(p.iframes, secs(0.1));
        out.land = Math.max(out.land, 8);
      }
    } else if (p.dodgeK === DodgeK.Dash) {
      const t = 1 - p.dodgeT / DASH_TICKS;
      const s = DASH_SPEED * (1 - 0.45 * t * t);
      p.vx = p.dodgeX * s; p.vz = p.dodgeZ * s;
      if (!onGround) p.vy -= GRAVITY * DT;
    } else if (p.dodgeK === DodgeK.Roll) {
      // Godot's roll is a flat 9 m/s for the whole 0.9 s (8.1 m); only the last beat, the get-up, slows
      const t = 1 - p.dodgeT / ROLL_TICKS;
      const s = p.rollV * (t < 0.85 ? 1 : 1 - 0.6 * ((t - 0.85) / 0.15));
      // a little steering through the roll
      if (moving) { const k = 0.06; p.dodgeX += (wx - p.dodgeX) * k; p.dodgeZ += (wz - p.dodgeZ) * k; const l = len2(p.dodgeX, p.dodgeZ) || 1; p.dodgeX /= l; p.dodgeZ /= l; }
      p.vx = p.dodgeX * s; p.vz = p.dodgeZ * s;
      if (!onGround) p.vy -= GRAVITY * DT;
      // late in the roll a jump cancels it (the roll-jump keeps its speed)
      if (p.dodgeT <= ROLL_TICKS - ROLL_CANCEL && (pressed & Btn.Jump) && onGround && alive) {
        p.dodgeT = 0; p.vy = JUMP_V; p.grounded = 0; out.jump = true; p.jumpHold = 0;
      }
    } else {
      // the hover dodge: flat and fast, gravity held off
      const t = 1 - p.dodgeT / HOVER_DASH_TICKS;
      const s = HOVER_DASH_SPEED * (1 - 0.6 * t * t);
      p.vx = p.dodgeX * s; p.vz = p.dodgeZ * s;
      p.vy *= 0.8; // a rise is damped, never reset: dodging can't hold you up
    }
    if (p.dodgeT <= 0 && p.move === Move.Dodge) {
      p.dodgeCd = Math.round((p.dodgeK === DodgeK.Dive ? DIVE_CD : p.dodgeK === DodgeK.HoverDash ? DODGE_CD : p.slide ? SLIDE_CD : ROLL_CD) * m.dodgeCdMul * (lowHeat ? m.lowHeatMul : 1));
      if (p.grounded) p.move = Move.Ground; // grounded, not onGround: a roll-jump has just left the floor
      else if (p.dodgeK === DodgeK.HoverDash && p.hoverMode) { p.move = Move.Hover; p.hoverY = p.y; }
      else p.move = Move.Air;
      if (p.dodgeK === DodgeK.HoverDash) { p.vx *= 0.55; p.vz *= 0.55; }
      p.slide = 0;
    }
  } else if (p.move === Move.Special) {
    p.specialT--;
    const fwdX = -Math.sin(p.yaw), fwdZ = -Math.cos(p.yaw);
    if (p.cls === ClassId.Phantom) { p.vx = 0; p.vz = 0; } // already there: the warp is a teleport
    else if (p.cls === ClassId.Valkyrie) {
      const cp = Math.cos(p.pitch);
      const air = !onGround;
      p.vx = fwdX * VDASH_SPEED * (air ? cp : 1); p.vz = fwdZ * VDASH_SPEED * (air ? cp : 1);
      p.vy = air ? Math.sin(p.pitch) * VDASH_SPEED : 0;
    } else { p.vx = fwdX * 17; p.vz = fwdZ * 17; if (!onGround) p.vy -= GRAVITY * DT; }
    if (p.specialT <= 0) {
      p.move = onGround ? Move.Ground : Move.Air;
    }
  } else if (p.move === Move.Flight) {
    // ---- traversal: holstered, superman, look-steered, momentum
    if (p.reloadT > 0 && !weaponOf(p).rounds) p.reloadT = 0;
    p.chargeT = 0; p.burstN = 0;
    // Nick 2026-10-22: the LEFT stick steers flight. Left / right TURNS the line of flight at the class turn rate
    // (it used to slip sideways); forward is the throttle and back the brake, as before. Pitch is the look (the right
    // stick), and the camera chases the line of flight while that stick is idle, so turning with the left stick
    // reads as flying. (Forward on the stick stays "go": a stick that dived on push made every go a dive.)
    let speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy + p.vz * p.vz);
    let hx = p.vx, hz = p.vz;
    if (len2(hx, hz) < 0.5) { hx = -Math.sin(inp.yaw); hz = -Math.cos(inp.yaw); }
    let hyaw = Math.atan2(-hx, -hz);
    const turning = Math.abs(inp.mx) > 0.15;
    // the line of flight's yaw: the look's, or where the stick has turned it (within 100 degrees of the look, so
    // the camera chase can always find it)
    if (turning) { hyaw -= inp.mx * c.turnRate * DT; const d = wrapAngle(hyaw - inp.yaw); if (Math.abs(d) > 1.75) hyaw = inp.yaw + Math.sign(d) * 1.75; }
    else hyaw = inp.yaw;
    const cp = Math.cos(inp.pitch);
    const dx = -Math.sin(hyaw) * cp, dy = Math.sin(inp.pitch), dz = -Math.cos(hyaw) * cp;
    const diving = dy < -0.25;
    const held = inp.my > 0.15;
    const throttle = held ? FLIGHT_GLIDE + (FLIGHT_SPEED - FLIGHT_GLIDE) * Math.min(1, (inp.my - 0.15) / 0.6)
      : inp.my < -0.3 ? FLIGHT_GLIDE * 0.5 : FLIGHT_GLIDE;
    const target = diving ? Math.max(throttle, FLIGHT_SPEED) + (FLIGHT_DIVE_SPEED - FLIGHT_SPEED) * Math.min(1, (-dy - 0.25) / 0.6)
      : throttle * (1 - Math.max(0, dy) * (1 - FLIGHT_CLIMB_MUL));
    // the speed law (flight_model.gd _integrate_speed): eased thrust against quadratic drag toward the pitch
    // target, 0 -> 95 % of cruise in about 1.27 s for the Gunslinger; a separate, smaller deceleration that
    // mostly stands down while the stick is held, so a pull-out keeps its dive speed
    const err = target - speed;
    if (err > 0) {
      const a = c.thrust * Math.min(1, err / ACCEL_EASE_REF) / (1 + DRAG_K * (speed / FLIGHT_SPEED) ** 2);
      speed += Math.min(err, a * DT);
    } else {
      const d = (c.thrust / A_DECEL) * Math.min(1, -err / DECEL_EASE_REF) * (held ? DECEL_THROTTLE_GATE : 1) + OVERSPEED_DRAG * speed * speed;
      speed += Math.max(err, -d * DT);
    }
    // steer: rotate the velocity toward the look at the class turn rate (rad/s), a constant angular rate so a
    // switchback turns as fast as a gentle bank (a vector blend all but stalls near 180 degrees). Slower at dive
    // speed (it carries weight), and eased for small corrections so the line stays smooth.
    const vl = speed || 1;
    let ux = p.vx / vl, uy = p.vy / vl, uz = p.vz / vl;
    if (speed < 0.5) { ux = dx; uy = dy; uz = dz; }
    const dot = clamp(ux * dx + uy * dy + uz * dz, -1, 1);
    const theta = Math.acos(dot);
    if (theta > 1e-5) {
      const w = c.turnRate * (1.1 - 0.25 * Math.min(1, speed / FLIGHT_DIVE_SPEED)) * Math.min(1, 0.4 + theta / 0.26);
      const step = Math.min(theta, w * DT);
      // the turn axis' in-plane direction: the look minus its component along the velocity (any perpendicular
      // when they're exactly opposite)
      let px = dx - ux * dot, py = dy - uy * dot, pz = dz - uz * dot;
      let pl = Math.sqrt(px * px + py * py + pz * pz);
      if (pl < 1e-6) { px = -uz; py = 0; pz = ux; pl = Math.sqrt(px * px + pz * pz) || 1; }
      px /= pl; py /= pl; pz /= pl;
      const cs = Math.cos(step), sn = Math.sin(step);
      ux = ux * cs + px * sn; uy = uy * cs + py * sn; uz = uz * cs + pz * sn;
    }
    p.vx = ux * speed; p.vy = uy * speed; p.vz = uz * speed;
    // strafe input slips sideways (a bank), barrel roll jukes hard
    const rx = Math.cos(inp.yaw), rz = -Math.sin(inp.yaw);
    let side = 0; // the stick steers now (above); the barrel roll still jukes
    if (p.dodgeT > 0 && p.dodgeK === DodgeK.Barrel) {
      p.dodgeT--;
      const t = 1 - p.dodgeT / BARREL_TICKS;
      side = 0;
      const bs = BARREL_SIDE * Math.sqrt(c.thrust / 30.5); // the light packs juke harder (Godot 16/sqrt(mass))
      p.x += p.dodgeX * bs * Math.sin(Math.PI * t) * DT * 1.6;
      p.z += p.dodgeZ * bs * Math.sin(Math.PI * t) * DT * 1.6;
      if (p.dodgeT === 0) p.dodgeCd = DODGE_CD;
    }
    p.x += rx * side * DT; p.z += rz * side * DT;
    // heat (FLIGHT_DESIGN §3.1b INVARIANT 1: a dive -> climb cycle always nets heat). The pack never gets its
    // passive cooling while it burns; a climb costs more than a cruise; a dive pays a little back, never enough
    // to buy the altitude back.
    // One rate, flight_heat.gd rate_for: cruise, plus the climb arm (x climb^1.5), minus the dive arm (x the
    // class's heat build, so the heavy pack vents as hard as it burns). Flight holds the pack under the
    // fireball line: at FLIGHT_CEIL the engine sputters out (a flameout, not a fire).
    const climb = Math.max(0, Math.min(1, dy)), dive = Math.max(0, Math.min(1, -dy));
    const rate = c.flightHeat + c.climbHeat * Math.pow(climb, CLIMB_SHAPE) - diveDrain(dive) * (c.flightHeat / 5.5);
    p.heatGrace = Math.max(p.heatGrace, secs(c.coolDelay));
    if (rate >= 0) {
      if (p.heat + rate * heatMul(p) * DT >= c.heatMax * FLIGHT_CEIL) { p.heat = Math.max(p.heat, c.heatMax * FLIGHT_CEIL - 0.01); flameout(p, out); p.move = Move.Air; out.denied = true; }
      else addHeat(p, rate * DT);
    } else p.heat = Math.max(0, p.heat + rate * DT);
  } else if (p.move === Move.Hover && !onGround) {
    // ---- hover: guns out, stable, slow drift, altitude hold with trim from the look pitch
    p.hoverT++;
    if (p.stallT > 0) {
      // the stall: brake hard but never to a snap; the body rights itself over this window
      p.stallT--;
      const kb = Math.exp(-DT * 7.5);
      p.vx *= kb; p.vz *= kb;
      p.vy += clamp(-p.vy, -40 * DT, 40 * DT);
      p.hoverY = p.y + p.vy * 0.15;
    } else {
      const trim = Math.abs(inp.pitch) > HOVER_TRIM_DZ ? Math.sign(inp.pitch) * (Math.abs(inp.pitch) - HOVER_TRIM_DZ) / (1.4 - HOVER_TRIM_DZ) : 0;
      p.hoverY += trim * HOVER_TRIM * (p.cls === ClassId.Valkyrie ? 2 : 1) * DT;
      const err = p.hoverY - p.y;
      const tvy = clamp(err * 4, -4, 6);
      p.vy += clamp(tvy - p.vy, -c.hoverLift * DT, c.hoverLift * DT);
      airControl(p, wx, wz, (aimHeld ? 0.7 : 1) * HOVER_DRIFT * slow, HOVER_ACCEL);
    }
    addHeat(p, c.hoverHeat * DT);
  } else if (!onGround && p.pound) {
    // ---- the Valkyrie's ground pound: straight down at POUND_FALL, nothing else, until the floor
    if (p.move !== Move.Air) p.move = Move.Air;
    p.hoverT = 0; p.hoverMode = 0;
    p.vx = 0; p.vz = 0; p.vy = -POUND_FALL;
  } else if (!onGround) {
    // ---- ballistic: jump arc, falls, after cutting the engine
    if (p.move !== Move.Air) p.move = Move.Air;
    p.hoverT = 0;
    p.vy -= (p.vy > 0 ? GRAVITY : FALL_GRAVITY) * DT;
    if (p.vy < -TERMINAL) p.vy = -TERMINAL;
    airControl(p, wx, wz, AIR_SPEED * slow, AIR_ACCEL);
    if (p.vy < -12) p.heat = Math.max(0, p.heat - 6 * DT); // falling vents heat
    if (p.coyote > 0) p.coyote--;
    if ((pressed & Btn.Jump) && alive) p.jumpBuf = JUMP_BUFFER;
    if (p.coyote > 0 && (pressed & Btn.Jump) && alive && p.vy <= 0) {
      p.vy = JUMP_V; p.coyote = 0; p.jumpBuf = 0; out.jump = true;
    }
  } else {
    // ---- on foot
    if (p.move !== Move.Land) p.move = Move.Ground;
    p.hoverT = 0; p.hoverMode = 0;
    // directional top speed: forward is fastest, then strafe, then backpedal (the clips read honestly)
    const fwdC = wl > 0.01 ? inp.my / wl : 1;
    const dirMul = fwdC >= 0 ? STRAFE_MUL + (1 - STRAFE_MUL) * fwdC : STRAFE_MUL + (BACK_MUL - STRAFE_MUL) * -fwdC;
    let top = (p.sprint ? SPRINT_SPEED * m.sprintMul : aimHeld ? ADS_SPEED : RUN_SPEED * dirMul) * m.moveMul * (p.cloakT > 0 ? m.cloakMoveMul : 1);
    if (p.landT > 0) top *= p.landK === LandK.Hero ? (heroLocked ? 0 : 0.45) : p.landK === LandK.Prone ? 0 : 0.7;
    // prone after a dive: the belly slide runs out under its own friction, then the get-up (nothing answers the pad)
    if (proneLocked && p.landT > PRONE_GETUP && len2(p.vx, p.vz) < 0.4 && p.landT <= PRONE_MAX + PRONE_GETUP - PRONE_MIN) p.landT = PRONE_GETUP;
    if (p.reloadT > 0 && !p.sprint) top *= 0.85;
    if (p.stimT > 0) top *= ADREN_SPEED; // whiskey courage
    if (wading) top *= WADE_MUL;
    const tx = wx * top * crawl * slow, tz = wz * top * crawl * slow;
    const dvx = tx - p.vx, dvz = tz - p.vz;
    const dl = len2(dvx, dvz);
    const cur = len2(p.vx, p.vz);
    // reversing or cutting hard gets more bite than speeding up in a straight line
    const against = cur > 1 && moving ? (p.vx * tx + p.vz * tz) / (cur * (len2(tx, tz) || 1)) : 1;
    const acc = (proneLocked ? PRONE_FRICTION : stunned || p.knockT > 0 ? KNOCK_FRICTION : moving ? (against < 0.5 ? GROUND_TURN_ACCEL : GROUND_ACCEL) : GROUND_FRICTION) * DT;
    if (dl <= acc) { p.vx = tx; p.vz = tz; } else { p.vx += (dvx / dl) * acc; p.vz += (dvz / dl) * acc; }
    p.coyote = COYOTE;
    if ((pressed & Btn.Jump) && alive) p.jumpBuf = JUMP_BUFFER;
    const canJump = alive && !heroLocked && p.jumpBuf > 0;
    if (canJump) {
      p.vy = JUMP_V; p.grounded = 0; p.jumpBuf = 0; p.coyote = 0; out.jump = true;
      p.landT = 0; p.landK = LandK.None; p.move = Move.Air;
      p.jumpHold = 0; // the hold toward ignition counts from the jump
      if (p.sprint) { p.vx *= 1.04; p.vz *= 1.04; }
    } else if (((pressed & Btn.Fly) || hoverPress) && alive && !heroLocked) {
      // ground launch: Fly goes straight to traversal, L1 pops up into a hover
      if (!p.onFire && canSpend(p, LAUNCH_HEAT)) {
        addHeat(p, LAUNCH_HEAT);
        p.grounded = 0; out.launched = true; p.landT = 0; p.landK = LandK.None;
        if (pressed & Btn.Fly) { p.vy = LAUNCH_V; p.vx *= 1.3; p.vz *= 1.3; p.move = Move.Flight; }
        else { p.vy = HOVER_POP_V; p.move = Move.Hover; p.hoverMode = 1; p.stallT = 0; p.hoverY = p.y + 3.2; }
      } else out.denied = true;
    }
  }

  // ---- fall tracking (armed only after standing on something): a powered pack holds the altitude, so the fall
  // counts from where the pack last flew or where you left the ground, whichever is higher
  // anything that moved us outside the sim since last tick (warp gate, saloon launch, scripted drop) places
  // us: a placed body has no departure altitude, so it can't have fallen
  if (Math.abs(p.y - p.stepY) > 3) p.fallTopY = -1e9;
  if (p.fallTopY > -1e8) {
    if (p.move === Move.Flight || p.move === Move.Hover) p.fallTopY = p.y;
    else if (p.y > p.fallTopY) p.fallTopY = p.y;
  }

  // ---- integrate + collide
  const prevY = p.y;
  const wasFlight = p.move === Move.Flight;
  const fallV = -p.vy;
  const flightSpeed = len3(p.vx, p.vy, p.vz);
  const ox = p.x, oz = p.z, vx0 = p.vx, vz0 = p.vz;
  let bonked = false;
  // a house wall doesn't stop a cowboy flying at full tilt: he goes through it (James, 2026-10-24, sim/destruct.ts BREACH).
  // Once he's inside he keeps going whatever his speed, until he's out the other side or lands.
  const inB0 = world.dest.buildingAt(ox, prevY + 0.9, oz);
  const smash = p.grounded === 0 && alive && ((wasFlight && len2(vx0, vz0) >= BREACH.speed) || inB0 >= 0);
  p.x += p.vx * DT;
  p.z += p.vz * DT;
  p.y += p.vy * DT;
  // cliffs (butte walls, canyon sides) are walls: slide along them, fly over them
  const foot = Math.max(prevY, p.y);
  if (world.cliff(ox, oz, foot, p.x, p.z)) {
    if (!world.cliff(ox, oz, foot, p.x, oz)) { p.z = oz; p.vz = 0; }
    else if (!world.cliff(ox, oz, foot, ox, p.z)) { p.x = ox; p.vx = 0; }
    else { p.x = ox; p.z = oz; p.vx = 0; p.vz = 0; }
    if (len2(p.vx, p.vz) < 0.3 * len2(vx0, vz0)) bonked = true; // nearly all the speed went into the wall: head-on, not a graze along it
  }
  // the underside of a shelf or an arch is a ceiling, not a wall to be shoved sideways out of
  if (p.vy > 0) { const cy = world.ceiling(p.x, p.z, p.y, PLAYER_HEIGHT, prevY); if (cy < p.y) { p.y = cy; p.vy = 0; } }
  // a fast fall onto a spire crown or a shelf top lands on it, rather than tunnelling in and being shoved off sideways
  if (p.vy < 0) { const fl = world.support(p.x, p.z, prevY, 0.05); if (fl > p.y) p.y = fl; }
  { const bx = p.x, bz = p.z; world.pushOut(p, PLAYER_RADIUS, PLAYER_HEIGHT, smash ? 'building' : null); const px = p.x - bx, pz = p.z - bz, pl = len2(px, pz), vl = len2(vx0, vz0) || 1; if (pl > 0.03 && (px * vx0 + pz * vz0) / (pl * vl) < -0.6) bonked = true; } // shoved straight back against the line of flight: a head-on, not a graze along a wall
  if (smash) {
    const inB = world.dest.buildingAt(p.x, p.y + 0.9, p.z);
    if (inB >= 0 && inB !== inB0) { out.breach = inB; p.vx *= BREACH.keep; p.vz *= BREACH.keep; }
    if (inB0 >= 0 && inB !== inB0) out.breachOut = inB0;
  }
  if (solids.length) pushOutOfSolids(p, solids);
  // a lighter knock: nearly all the way stopped by something, on foot or slow in the air. It's only felt (sim/game.ts ignores it)
  if (alive && !bonked && out.breach < 0) {
    const v0 = len2(vx0, vz0), v1 = len2(p.x - ox, p.z - oz) / DT;
    if (v0 >= BUMP_MIN && v1 < 0.35 * v0 && (p.knockCd === 0 || !wasFlight)) out.bump = v0;
  }
  if (out.bump === 0 && bonked && alive && !(wasFlight && len2(vx0, vz0) > BONK_MIN)) out.bump = len2(vx0, vz0) >= BUMP_MIN ? len2(vx0, vz0) : 0;
  // flying into a wall or a building: past BONK_MIN it hurts, knocks you out of flight and bounces you off (Nick, playtest 2)
  if (wasFlight && bonked && alive && len2(vx0, vz0) > BONK_MIN && p.knockCd === 0) {
    out.bonk = len2(vx0, vz0); out.bonkX = vx0 / out.bonk; out.bonkZ = vz0 / out.bonk; p.knockCd = secs(1.5); // one bonk, then a beat of grace (the weight lane's knock clock)
    p.move = Move.Air; p.hoverMode = 0; p.stallT = 0;
    p.vx = -vx0 * 0.25; p.vz = -vz0 * 0.25; p.vy = Math.min(p.vy, 1.5);
    p.hitStunT = Math.max(p.hitStunT, secs(0.5)); p.jumpHold = 0;
  }
  const ground = world.support(p.x, p.z, Math.max(prevY, p.y));
  if (ground !== NO_GROUND && p.y <= ground + 0.02 && p.vy <= 0.5) {
    if (!p.grounded) {
      if (p.vy < -6) out.land = -p.vy;
      // FLIGHT_DESIGN §3.1b INVARIANT 2: an unpowered fall past FALL_FREE_M hurts, scaled by height, unless the
      // pack's remaining capacity (up to 78% of the bar, none while on fire) cushions it, landing you hot.
      // fallTopY is armed only once you've stood on something, so spawns and rescues can't count as falls.
      if (RULES.fallDamage && alive && p.fallTopY > -1e8) {
        const drop = p.fallTopY - ground;
        const raw = FALL_DAMAGE_PER_M * Math.max(0, drop - FALL_FREE_M);
        if (raw > 0) {
          const headroom = p.onFire ? 0 : Math.max(0, CUSHION_HEAT_CEIL * c.heatMax - p.heat);
          const eaten = Math.min(raw, headroom * CUSHION_HP_PER_HEAT);
          if (eaten > 0) addHeat(p, eaten / CUSHION_HP_PER_HEAT);
          out.fallDmg = raw - eaten;
        }
      }
      // landings: a big fall or a fast diving arrival from flight ends in the superhero three-point landing
      const hero = alive && p.move !== Move.Dodge && (fallV > HARD_LAND_V || (wasFlight && flightSpeed > 19 && fallV > HERO_FLIGHT_SINK));
      if (hero) {
        p.landK = LandK.Hero; p.landT = HERO_LAND_TICKS; p.move = Move.Land;
        p.vx *= 0.15; p.vz *= 0.15; p.sprint = 0;
        out.land = Math.max(out.land, HARD_LAND_V);
      } else if (fallV > 11 && p.move !== Move.Dodge) { p.landK = LandK.Soft; p.landT = SOFT_LAND_TICKS; }
    }
    p.y = ground;
    if (p.vy < 0) p.vy = 0;
    p.grounded = 1;
    if (p.pound) { p.pound = 0; out.poundLand = true; p.landK = LandK.Hero; p.landT = HERO_LAND_TICKS; p.move = Move.Land; }
    // Sky-Stalker's limiter: the Valkyrie's chain window lives in the air and touching down closes it
    if (p.cls === ClassId.Valkyrie && p.chainT > 0) {
      if (m.skySlam) out.skySlam = true; // Downdraft
      if (m.skyIcarus) p.heat = c.heatMax; // Icarus Pact: the ground is the price
      if (m.skyLandings > 0 && !p.skyLandUsed) p.skyLandUsed = 1; else { p.chainT = 0; p.chainN = 0; }
    }
    p.airT = 0; p.airDashN = 0;
    p.fallTopY = p.y;
    p.hoverMode = 0; p.stallT = 0;
    if (p.move === Move.Flight || p.move === Move.Hover || p.move === Move.Air) p.move = Move.Ground;
    // remember a safe spot (gotcha 8: deaths come back to the cliff edge, not the void)
    if (p.phase === Phase.Alive && ground > -50) {
      const isl = world.islandAt(p.x, p.z);
      if (isl) { p.lastGx = p.x; p.lastGy = p.y; p.lastGz = p.z; }
    }
  } else {
    // walked off a ledge, or a step down
    if (p.grounded && ground !== NO_GROUND && p.y - ground < 0.6 && p.vy <= 0) {
      p.y = ground; // stick to slopes and steps
    } else {
      if (p.grounded && p.move === Move.Ground) p.move = Move.Air;
      p.grounded = 0;
      p.airT++;
    }
  }
  // ceiling: the world ends at the haze
  if (p.y > 95) { p.y = 95; if (p.vy > 0) p.vy = 0; }

  // ---- water: wading, skimming and waterfalls dump heat and put out a burning pack
  if (alive) {
    const wy = world.waterAt(p.x, p.z);
    let cool = 0, douse = false;
    if (wy !== NO_GROUND) {
      const above = p.y - wy;
      if (above < 0.35) { cool = WADE_COOL; douse = true; p.wetT = secs(4); }
      else if (!p.grounded && above < WATER.skimH) cool = SKIM_COOL * (1 - above / WATER.skimH);
    }
    const fall = world.inFall(p.x, p.y + 1, p.z);
    if (fall > 0) { cool = Math.max(cool, FALL_COOL * fall); douse = true; }
    if (cool > 0) {
      p.heat = Math.max(0, p.heat - cool * DT);
      if (douse && p.onFire) p.onFire = 0;
      if (douse) p.burnT = 0;
    }
    if (!douse && p.wetT > 0) p.wetT--;
  }

  // fell off the world: back to the last safe ground, it costs hull
  if (p.y < KILL_Y) {
    p.x = p.lastGx; p.y = p.lastGy + 0.1; p.z = p.lastGz;
    p.vx = p.vy = p.vz = 0;
    p.grounded = 1; p.move = Move.Ground; p.hoverMode = 0;
    p.fallTopY = -1e9; // a rescue is never also a fall (gotcha 8: a life OR a rescue, never both)
    if (p.phase === Phase.Alive) {
      if (m.eagle && !p.eagleUsed) p.eagleUsed = 1; else p.hp -= 20; // Golden Eagle: one free rescue
      p.iframes = secs(1);
      if (p.hp <= 0) { p.hp = 0; p.phase = Phase.Down; p.phaseT = secs(30); }
    }
  }

  p.stepY = p.y;

  // timers that belong to one mode die with it: a stall left over after dropping out of hover would lock
  // the guns until landing, and an unfinished barrel roll would resume on the next ignite
  if (p.move !== Move.Hover) p.stallT = 0;
  if (p.move !== Move.Flight && p.dodgeK === DodgeK.Barrel && p.move !== Move.Dodge) p.dodgeT = 0;

  if (!alive) { p.reloadT = 0; p.blade = 0; p.combo = 0; p.comboT = 0; p.meleeBuf = 0; return out; }

  // ---- melee (primer verbs) with the class energy blade: the first press puts the gun away and draws the
  // blade, and the draw is itself a strike; more presses chain the class's flourishes while it's out. Damage,
  // priming and cadence are the same on every step. Any gun verb (fire, aim, reload, swap, grenade) or taking
  // off puts the blade away and the gun comes back.
  if (p.comboT > 0) p.comboT--;
  if (p.meleeBuf > 0) p.meleeBuf--;
  // R3 held (CLASSES.md): the Phantom's jab fires on the press and the blink on a BLINK_HOLD hold; the Juggernaut's
  // press starts a wind-up, a tap (let go inside BASH_TAP) is the bash, a hold is the charged hammer, released or
  // at HAMMER_MAX; the Valkyrie's R3 in the air is the ground pound (needs some shield)
  const meleeHeld = (b & Btn.Melee) !== 0;
  if (p.cls === ClassId.Juggernaut) {
    // the bash lands on the press (a web keeper: Godot waited BASH_TAP for the release); keep R3 held past the
    // wind-up and the hammer charges on top, swung on release or at full charge
    if ((pressed & Btn.Melee) && p.move !== Move.Flight) p.meleeBuf = MELEE_BUFFER;
    if (meleeHeld && p.move !== Move.Flight) {
      if (++p.meleeHold >= HAMMER_MAX && p.meleeCd <= MELEE_CD) { out.hammer = 1; p.meleeHold = 0; }
    } else if (p.meleeHold > 0) {
      if (p.meleeHold >= HAMMER_WINDUP && p.meleeCd <= MELEE_CD) out.hammer = clamp((p.meleeHold - HAMMER_WINDUP) / (HAMMER_MAX - HAMMER_WINDUP), 0, 1);
      p.meleeHold = 0;
    }
    if (out.hammer >= 0) { p.meleeT = secs(0.3); p.blade = 1; }
  } else if (p.cls === ClassId.Phantom) {
    if ((pressed & Btn.Melee) && p.move !== Move.Flight) p.meleeBuf = MELEE_BUFFER;
    if (meleeHeld && p.move !== Move.Flight) { if (++p.meleeHold === BLINK_HOLD && p.meleeCd <= MELEE_CD) out.blink = true; }
    else p.meleeHold = 0;
  } else if (p.cls === ClassId.Valkyrie && (pressed & Btn.Melee) && !onGround && !p.pound && p.shield > 0 && p.move !== Move.Dodge && p.move !== Move.Special) {
    p.pound = 1; p.poundY = p.y; p.move = Move.Air; p.hoverMode = 0; p.stallT = 0; p.meleeCd = MELEE_CD;
    p.blade = 0; p.combo = 0; p.comboT = 0; p.meleeBuf = 0;
  } else if ((pressed & Btn.Melee) && p.move !== Move.Flight) p.meleeBuf = MELEE_BUFFER;
  if (p.meleeBuf > 0 && p.meleeCd === 0 && p.move !== Move.Flight) {
    p.meleeBuf = 0;
    p.combo = !p.blade ? 0 : p.comboT > 0 ? (p.combo % COMBO_STEPS) + 1 : 1;
    p.blade = 1;
    p.meleeCd = p.stimT > 0 ? Math.round(MELEE_CD * ADREN_MELEE_CD) : MELEE_CD;
    p.meleeT = secs(0.25);
    p.comboT = MELEE_CD + COMBO_WINDOW;
    out.melee = true;
    if (p.cloakT > 0 && p.cls !== ClassId.Phantom) p.cloakT = 0;
  }
  if (p.blade && (((pressed | (b & Btn.Aim)) & (Btn.Fire | Btn.Aim | Btn.Reload | Btn.Swap | Btn.SpecialWeapon | Btn.Throw)) !== 0 || p.move === Move.Flight)) {
    p.blade = 0; p.combo = 0; p.comboT = 0; p.meleeBuf = 0;
  }

  // ---- loadout: swap (D-pad →), pull the special (D-pad ↑), throw a grenade (R1)
  if ((pressed & Btn.Swap) && p.move !== Move.Special) {
    const to = p.wSlot === WSlot.Primary ? WSlot.Secondary : WSlot.Primary;
    if (switchSlot(p, to)) out.swap = true;
  }
  if ((pressed & Btn.SpecialWeapon) && p.move !== Move.Special) {
    if (switchSlot(p, p.wSlot === WSlot.Special ? p.prevSlot : WSlot.Special)) out.swap = true;
  }
  // fused grenades cook while R1 is held and leave the hand on release; impact grenades go on the press
  const fuse = secs(GRENADES[p.nade]?.fuse ?? 0);
  const letGo = () => {
    p.nades--; p.throwCd = THROW_CD;
    if (p.reloadT > 0 && !weaponOf(p).rounds) p.reloadT = 0;
    p.chargeT = 0; p.burstN = 0;
    p.fireCd = Math.max(p.fireCd, 12);
    if (p.cloakT > 0 && p.cls === ClassId.Phantom) p.cloakT = 0; // a grenade breaks the dance
  };
  if (p.cookT > 0) {
    p.fireCd = Math.max(p.fireCd, 2); // the gun hand is busy with the pin
    if (++p.cookT >= fuse) { letGo(); p.cookT = 0; out.cookOff = true; } // held it too long
    else if (!(b & Btn.Throw)) { letGo(); out.cook = p.cookT; p.cookT = 0; out.throw = true; }
  } else if ((pressed & Btn.Throw) && p.move !== Move.Flight && p.move !== Move.Special) {
    if (p.nades > 0 && p.throwCd === 0 && p.meleeT === 0) { // (the belt's cap is GRENADE_MAX + the hand's nadeAdd: arsenal.ts)
      if (fuse > 0) { p.cookT = 1; p.fireCd = Math.max(p.fireCd, 2); }
      else { letGo(); out.throw = true; }
    } else out.denied = true;
  }

  // ---- reload: heat is paid on reload, never on firing (GAMEPLAY_LOOP §1.5)
  const w = weaponOf(p);
  const special = p.wSlot === WSlot.Special;
  if (special) {
    // specials don't reload: they recharge off your shield (arsenal.ts); out of shots, put it away
    p.reloadT = 0;
    if (p.mag === 0 && p.fireCd === 0 && (pressed & Btn.Fire)) { out.denied = true; switchSlot(p, p.prevSlot); out.swap = true; }
  } else if (p.reloadT > 0) {
    p.reloadT--;
    if (w.rounds) {
      if ((b & Btn.Fire) && p.mag > 0) p.reloadT = 0; // cancel to shoot
      else if (p.reloadT === 0) {
        if (p.mag === 0 && w.emptyShove > 0) out.shove = true;
        p.mag++;
        if (p.mag < w.mag) {
          if (!p.onFire && canSpend(p, w.reloadHeat * reloadHeatMul(p, m))) { addHeat(p, w.reloadHeat * reloadHeatMul(p, m)); p.reloadT = Math.round(w.reloadTicks * m.reloadMul * (lowHeat ? m.lowHeatMul : 1)); }
          else out.denied = true;
        }
      }
    } else if (p.reloadT === 0) p.mag = w.mag;
  } else if (((pressed & Btn.Reload) || ((pressed & Btn.Fire) && p.mag === 0)) && p.mag < w.mag && p.move !== Move.Flight) {
    if (!p.onFire && canSpend(p, w.reloadHeat * reloadHeatMul(p, m))) {
      addHeat(p, w.reloadHeat * reloadHeatMul(p, m));
      p.reloadT = Math.round(w.reloadTicks * m.reloadMul * (lowHeat ? m.lowHeatMul : 1));
      p.chargeT = 0; p.burstN = 0;
      out.reload = true;
    } else out.denied = true;
  }

  // ---- fire (never in traversal flight: guns are holstered; not while tucked in a roll, planted in a
  // superhero landing, or in the first beat of the stall while the guns come out)
  const rollTucked = p.move === Move.Dodge && p.dodgeK === DodgeK.Roll && p.dodgeT > ROLL_TICKS - ROLL_CANCEL;
  const planted = p.landK === LandK.Hero && p.landT > HERO_LAND_TICKS - HERO_LOCK;
  const ready = p.mag > 0 && p.reloadT === 0 && p.move !== Move.Flight && p.move !== Move.Special && p.meleeT === 0
    && !rollTucked && !planted && p.stallT < secs(0.2);
  const held = (b & Btn.Fire) !== 0;
  const aimed = p.aimOn === 1;
  // ---- gun identities that run every tick (GUN_IDENTITIES.md)
  if (w.perk === Perk.SpinUp) p.spin = held && ready ? Math.min(SPIN_TICKS, p.spin + 1) : Math.max(0, p.spin - 2);
  else p.spin = 0;
  if (w.perk === Perk.Steady) p.steady = aimed && p.reloadT === 0 && p.move !== Move.Flight ? Math.min(STEADY_TICKS, p.steady + 1) : 0;
  else p.steady = 0;
  p.bloom = Math.max(0, p.bloom - FAN_BLEED / 60);
  let shoot = false;
  let mod: number = ShotMod.Normal, mul = 1;
  switch (w.fire) {
    case Fire.Semi: shoot = (pressed & Btn.Fire) !== 0 && p.fireCd === 0; break;
    case Fire.Auto: shoot = held && p.fireCd === 0; break;
    case Fire.Burst:
      if (p.burstN > 0) { if (p.fireCd === 0) { shoot = true; p.burstN--; } }
      else if (held && p.fireCd === 0) { shoot = true; p.burstN = w.burst - 1; }
      break;
    case Fire.Charge:
      // hold to charge, release to fire. Plasma: a full charge locks on and tracks; an early release
      // still fires a straight bolt at partial power (a tap is never wasted)
      if (held && ready && p.fireCd === 0) p.chargeT = Math.min(p.chargeT + 1, w.charge + 60);
      else if (!held) {
        if ((released & Btn.Fire) && p.fireCd === 0) {
          if (p.chargeT >= w.charge) { shoot = true; if (w.perk === Perk.PlasmaLock) { mod = ShotMod.Locked; mul = PLASMA_FULL_MUL; } }
          else if (w.perk === Perk.PlasmaLock && p.chargeT >= PLASMA_MIN) {
            shoot = true; mod = ShotMod.Partial;
            mul = PLASMA_PARTIAL_MIN + (1 - PLASMA_PARTIAL_MIN) * (p.chargeT - PLASMA_MIN) / (w.charge - PLASMA_MIN);
          }
        }
        p.chargeT = 0;
      }
      break;
  }
  if (shoot && ready) {
    // a shot late in the roll ends it: come up firing
    if (p.move === Move.Dodge && p.dodgeK === DodgeK.Roll) { p.dodgeT = 0; p.dodgeCd = DODGE_CD; p.move = p.grounded ? Move.Ground : Move.Air; }
    if (!w.noAmmo) p.mag--;
    if (w.shotHeat) addHeat(p, w.shotHeat * mul); // the Duel Blaster: the pack is the magazine
    p.fireCd = Math.round((w.fire === Fire.Burst && p.burstN > 0 ? w.burstGap : w.interval) * m.fireIntervalMul * gunLevelRate(p, p.wSlot)); // Gunsmith
    p.shotN++; // Overcharge counts rounds
    let spreadMul = 1;
    switch (w.perk) {
      case Perk.SpinUp: {
        const k = p.spin / SPIN_TICKS;
        p.fireCd = Math.round(SPIN_SLOW + (SPIN_FAST - SPIN_SLOW) * k);
        spreadMul = 0.6 + 0.8 * k; // tight first rounds, a hose at full spin
        break;
      }
      case Perk.Steady: mul = 1 + STEADY_BONUS * p.steady / STEADY_TICKS; p.steady = 0; break;
      case Perk.Fan:
        spreadMul = 1 + p.bloom * FAN_SPREAD;
        if (!aimed) { p.fireCd = FAN_INTERVAL; p.bloom += FAN_BLOOM; }
        break;
      case Perk.DoubleBarrel:
        if (aimed && p.mag > 0) { p.mag--; mod = ShotMod.Double; }
        break;
    }
    if (p.mag === 0) p.burstN = 0;
    if (w.proj !== Proj.Hitscan && special && p.mag === 0) p.fireCd = Math.max(p.fireCd, 20);
    out.shots = 1; out.mod = mod; out.mul = mul; out.spreadMul = spreadMul;
  } else if (shoot && p.mag === 0) p.burstN = 0;
  return out;
}

function airControl(p: PlayerState, wx: number, wz: number, maxSpeed: number, accel: number) {
  const tx = wx * maxSpeed, tz = wz * maxSpeed;
  const cur = len2(p.vx, p.vz);
  // never brake momentum you already have; only steer and add up to maxSpeed
  const dvx = tx - p.vx, dvz = tz - p.vz;
  const dl = len2(dvx, dvz);
  if (dl < 1e-4) return;
  const a = Math.min(dl, accel * DT);
  const nvx = p.vx + (dvx / dl) * a, nvz = p.vz + (dvz / dl) * a;
  const nl = len2(nvx, nvz);
  if (nl > Math.max(cur, maxSpeed) && nl > 0) {
    const s = Math.max(cur, maxSpeed) / nl;
    p.vx = nvx * s; p.vz = nvz * s;
  } else { p.vx = nvx; p.vz = nvz; }
  // gentle air drag so flight momentum bleeds after the stall
  if (cur > maxSpeed) { p.vx *= 0.985; p.vz *= 0.985; }
}

export function eyePos(p: PlayerState) {
  return { x: p.x, y: p.y + EYE_HEIGHT + p.skyT * p.skyT * 45, z: p.z };
}
export function renderY(p: PlayerState) {
  return p.y + p.skyT * p.skyT * 45;
}
export function isTargetable(p: PlayerState) {
  return p.phase !== Phase.Dead && !p.paused && p.skyT < 0.35;
}
/** a cloaked Phantom: robots can't pick it as a target (they still walk into its bullets) */
export const isHidden = (p: PlayerState) => p.cloakT > 0;
